import type { CountryCode } from "@/config/country-config";
import type { Debts } from "@/types/form";

export type CountrySettings = {
	/** Cars and debt steps, for the applicant and the co-applicant. */
	collectsCarsAndDebt: boolean;
	/** Children under 18 on the civil status step. */
	asksChildren: boolean;
	/** Strong authentication (FTN) before submit. */
	requiresAuth: boolean;
	/** "Borrowing costs money" disclaimer on the landing page. */
	landingDebtWarning: boolean;
	/** Phone and national ID rules. NO uses the SE rules for now. */
	validationCountry: "SE" | "FI";
	loanAmount: { min: number; max: number; step: number; default: number };
	/** Per month. */
	minMonthlyIncome: number;
	/** Sanity checks only: applicants may continue past these amounts. */
	warnings: {
		/** Per month. */
		monthlyIncome: number;
		/** Per month. */
		housingCost: number;
		debt?: Record<keyof Debts, number>;
	};
	/** Receipt pages on the public site, by application outcome. */
	receiptUrls: { error: string; yellow: string; reject: string };
};

const SE = {
	collectsCarsAndDebt: false,
	asksChildren: true,
	requiresAuth: false,
	landingDebtWarning: true,
	validationCountry: "SE",
	loanAmount: { min: 10_000, max: 500_000, step: 1_000, default: 200_000 },
	minMonthlyIncome: 15_000,
	warnings: { monthlyIncome: 150_000, housingCost: 100_000 },
	receiptUrls: {
		error: "https://leabank.se/ansokan/kvitto5",
		yellow: "https://leabank.se/ansokan/kvitto2",
		reject: "https://leabank.se/ansokan/kvitto1",
	},
} as const satisfies CountrySettings;

const FI = {
	collectsCarsAndDebt: true,
	asksChildren: false,
	requiresAuth: true,
	landingDebtWarning: true,
	validationCountry: "FI",
	loanAmount: { min: 1_000, max: 50_000, step: 1_000, default: 10_000 },
	minMonthlyIncome: 1_500,
	warnings: {
		monthlyIncome: 15_000,
		housingCost: 10_000,
		debt: {
			mortgageLoan: 1_000_000,
			vehicleLoan: 100_000,
			studentLoan: 50_000,
			consumerLoan: 50_000,
		},
	},
	receiptUrls: {
		error: "https://leabank.fi/kuitti5",
		yellow: "https://leabank.fi/kuitti2",
		reject: "https://leabank.fi/kuitti1",
	},
} as const satisfies CountrySettings;

const NO = {
	...SE,
	landingDebtWarning: false,
	receiptUrls: {
		error: "https://leabank.no/soknad/kvittering5",
		yellow: "https://leabank.no/soknad/kvittering2",
		reject: "https://leabank.no/soknad/kvittering1",
	},
} as const satisfies CountrySettings;

export const COUNTRY_SETTINGS: Record<CountryCode, CountrySettings> = {
	SE,
	FI,
	NO,
};
